<template>
  <section class="warning-page">
    <div class="page-header">
      <div>
        <h2>预警配置</h2>
        <p>维护港口预警组合和通知公邮，配置保存后实时生效。</p>
      </div>
    </div>

    <div v-loading="loading" class="warning-card">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="港口预警配置" name="ports">
          <div class="section-head">
            <div>
              <h3>港口预警组合配置</h3>
              <p>最多配置 5 组起运港到目的港组合，命中后自动生成线索。</p>
            </div>
            <el-button type="primary" plain :disabled="portConfigs.length >= 5" @click="addPortConfig">新增组合</el-button>
          </div>

          <div v-if="!portConfigs.length" class="empty-box">当前暂无港口预警组合</div>

          <div v-for="(item, index) in portConfigs" :key="item.key" class="port-row">
            <div class="port-index">{{ index + 1 }}</div>
            <el-select
              v-model="item.polCode"
              filterable
              remote
              reserve-keyword
              clearable
              placeholder="请选择起运港"
              :remote-method="searchPortOptions"
              :loading="portOptionsLoading"
            >
              <el-option
                v-for="option in portOptions"
                :key="option.portCode"
                :label="formatPortOption(option)"
                :value="option.portCode"
              />
            </el-select>
            <span class="port-arrow">→</span>
            <el-select
              v-model="item.podCode"
              filterable
              remote
              reserve-keyword
              clearable
              placeholder="请选择目的港"
              :remote-method="searchPortOptions"
              :loading="portOptionsLoading"
            >
              <el-option
                v-for="option in portOptions"
                :key="option.portCode"
                :label="formatPortOption(option)"
                :value="option.portCode"
              />
            </el-select>
            <el-button type="danger" plain @click="removePortConfig(index)">删除</el-button>
          </div>
        </el-tab-pane>

        <el-tab-pane label="公邮地址维护" name="emails">
          <div class="section-head">
            <div>
              <h3>公邮地址列表</h3>
              <p>维护接收线索通知的邮箱地址，并确保存在一个默认公邮。</p>
            </div>
          </div>

          <div class="email-input-row">
            <el-input v-model="emailInput" placeholder="请输入新的邮箱地址" @keyup.enter="addEmail" />
            <el-button type="primary" plain @click="addEmail">添加</el-button>
          </div>

          <div v-if="!emailConfigs.length" class="empty-box">当前暂无通知邮箱</div>

          <div v-for="(item, index) in emailConfigs" :key="item.email" class="email-row">
            <div class="email-main">
              <strong>{{ item.email }}</strong>
              <el-tag v-if="item.isDefault === 1" type="success">默认公邮</el-tag>
            </div>
            <div class="email-actions">
              <el-button v-if="item.isDefault !== 1" link type="primary" @click="setDefaultEmail(index)">设为默认</el-button>
              <el-button link type="danger" @click="removeEmail(index)">删除</el-button>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <div class="footer-actions">
        <el-button @click="resetConfig">重置</el-button>
        <el-button type="primary" :loading="saving" @click="saveConfig">保存配置</el-button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as Request from '@/assets/request'

const activeTab = ref('ports')
const loading = ref(false)
const saving = ref(false)
const portOptionsLoading = ref(false)
const portOptions = ref([])
const portConfigs = ref([])
const emailConfigs = ref([])
const emailInput = ref('')

let portConfigSeed = 0

const formatPortOption = (item) => {
  if (!item) {
    return '-'
  }
  const primaryName = item.portCnName || item.portEnName || item.portCode
  return `${primaryName} (${item.portCode})`
}

const createPortConfig = (config = {}) => ({
  key: `port_${Date.now()}_${portConfigSeed++}`,
  polCode: config.polCode || '',
  polName: config.polName || '',
  podCode: config.podCode || '',
  podName: config.podName || '',
})

const ensurePortOption = (config = {}) => {
  const options = [
    { portCode: config.polCode, portCnName: config.polName, portEnName: config.polName },
    { portCode: config.podCode, portCnName: config.podName, portEnName: config.podName },
  ]
  options.forEach((option) => {
    if (!option.portCode) {
      return
    }
    const exists = portOptions.value.some((item) => item.portCode === option.portCode)
    if (!exists) {
      portOptions.value = [...portOptions.value, option]
    }
  })
}

const fetchDetail = async () => {
  loading.value = true
  const result = await Request.post({
    url: '/company/lead/warning/detail',
    data: {},
  })
  loading.value = false
  if (result?.status === 200) {
    const portList = result.data.portConfigs || []
    const emailList = result.data.emailConfigs || []
    portConfigs.value = portList.map((item) => createPortConfig(item))
    emailConfigs.value = emailList.map((item) => ({
      email: item.email || '',
      isDefault: item.isDefault === 1 ? 1 : 0,
    }))
    portList.forEach((item) => ensurePortOption(item))
  }
}

const searchPortOptions = async (keyword = '') => {
  portOptionsLoading.value = true
  const result = await Request.post({
    url: '/port/query',
    data: {
      portName: keyword || undefined,
      page: 1,
      size: 20,
    },
  })
  portOptionsLoading.value = false
  if (result?.status === 200) {
    portOptions.value = result.data.list || []
    portConfigs.value.forEach((item) => ensurePortOption(item))
  }
}

const addPortConfig = () => {
  if (portConfigs.value.length >= 5) {
    ElMessage.warning('最多只能配置 5 组港口组合')
    return
  }
  portConfigs.value = [...portConfigs.value, createPortConfig()]
}

const removePortConfig = async (index) => {
  try {
    await ElMessageBox.confirm('确认删除该港口预警组合吗？', '删除组合', {
      type: 'warning',
    })
    portConfigs.value.splice(index, 1)
  } catch (error) {
    return
  }
}

const addEmail = () => {
  const email = emailInput.value.trim().toLowerCase()
  if (!email) {
    ElMessage.warning('请输入邮箱地址')
    return
  }
  if (emailConfigs.value.some((item) => item.email === email)) {
    ElMessage.warning('该公邮已添加，请勿重复输入')
    return
  }
  emailConfigs.value = [
    ...emailConfigs.value,
    {
      email,
      isDefault: emailConfigs.value.length ? 0 : 1,
    },
  ]
  emailInput.value = ''
}

const setDefaultEmail = (index) => {
  emailConfigs.value = emailConfigs.value.map((item, currentIndex) => ({
    ...item,
    isDefault: currentIndex === index ? 1 : 0,
  }))
}

const removeEmail = async (index) => {
  try {
    await ElMessageBox.confirm('确认删除该公邮地址吗？', '删除公邮', {
      type: 'warning',
    })
    emailConfigs.value.splice(index, 1)
  } catch (error) {
    return
  }
}

const saveConfig = async () => {
  saving.value = true
  const result = await Request.post({
    url: '/company/lead/warning/save',
    data: {
      portConfigs: portConfigs.value.map((item) => ({
        polCode: item.polCode || undefined,
        polName: item.polName || undefined,
        podCode: item.podCode || undefined,
        podName: item.podName || undefined,
      })),
      emailConfigs: emailConfigs.value.map((item) => ({
        email: item.email,
        isDefault: item.isDefault,
      })),
    },
  })
  saving.value = false
  if (result?.status === 200) {
    ElMessage.success('预警配置保存成功')
    fetchDetail()
  }
}

const resetConfig = async () => {
  try {
    await ElMessageBox.confirm('确认重置全部预警配置吗？该操作会清空港口组合和公邮地址。', '重置配置', {
      type: 'warning',
    })
    const result = await Request.post({
      url: '/company/lead/warning/reset',
      data: {},
    })
    if (result?.status === 200) {
      ElMessage.success('配置已重置')
      portConfigs.value = []
      emailConfigs.value = []
      emailInput.value = ''
    }
  } catch (error) {
    return
  }
}

onMounted(async () => {
  await Promise.all([fetchDetail(), searchPortOptions()])
})
</script>

<style scoped lang="less">
.warning-page {
  .page-header {
    margin-bottom: 20px;

    h2 {
      margin-bottom: 4px;
      font-size: 20px;
      line-height: 28px;
      color: #1f2937;
    }

    p {
      font-size: 13px;
      color: #6b7280;
    }
  }

  .warning-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 20px 24px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  }

  .section-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    h3 {
      margin: 0 0 6px;
      font-size: 18px;
      color: #111827;
    }

    p {
      margin: 0;
      font-size: 13px;
      color: #6b7280;
    }
  }

  .empty-box {
    padding: 32px 16px;
    border: 1px dashed #d1d5db;
    border-radius: 12px;
    text-align: center;
    color: #9ca3af;
    margin-bottom: 16px;
    font-size: 13px;
  }

  :deep(.el-form-item__label) {
    font-size: 13px;
    color: #4b5563;
    padding-bottom: 4px;
  }

  :deep(.el-input__inner),
  :deep(.el-input) {
    font-size: 13px;
  }

  .port-row,
  .email-row,
  .email-input-row {
    display: grid;
    gap: 12px;
    align-items: center;
    margin-bottom: 16px;
  }

  .port-row {
    grid-template-columns: 48px minmax(0, 1fr) 32px minmax(0, 1fr) 88px;
  }

  .port-index {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: #991b1b;
    color: #fff;
    font-weight: 600;
  }

  .port-arrow {
    text-align: center;
    color: #6b7280;
    font-size: 18px;
  }

  .email-input-row {
    grid-template-columns: minmax(0, 1fr) 88px;
  }

  .email-row {
    grid-template-columns: minmax(0, 1fr) auto;
    padding: 14px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
  }

  .email-main {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    strong {
      color: #111827;
      word-break: break-all;
    }
  }

  .email-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 12px;
  }
}

@media (max-width: 900px) {
  .warning-page {
    .warning-card {
      padding: 16px;
    }

    .section-head,
    .port-row,
    .email-row,
    .email-input-row {
      grid-template-columns: 1fr;
    }

    .port-arrow {
      transform: rotate(90deg);
      padding: 8px 0;
    }

    .footer-actions {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>
